'use client';

import { Default_Image, base64Image } from '@/constants/index';
import QuickViewButton from '@/components/QuickViewButton';
import { useProductQuickView } from '@/hooks/useProductQuickView';
import { IProduct } from '@/interfaces/product';
import { numberFormat } from '@/utils/helper';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { ProductColorVariants } from '@/components/ProductColorVariants';
import CustomLink from './CustomLink';
import { IconFreeShipping } from '@/icons/IconFreeShipping';
import RotatingNeuroMessage from './RotatingNeuroMessage';
import { IconExpress } from '@/icons/IconRocket';

interface IProductCard {
  className?: string;
  name: string;
  category?: string;
  url: string;
  imageUrl: string;
  imageAlt?: string;
  imageAspectRatio?: string;
  options?: string;
  topRightIcon?: any;
  topRightIconClassName?: string;
  seName?: any;
  oldPrice?: number;
  price: number;
  isCategoryProduct?: boolean;
  colorVariants?: boolean;
  largeSize?: boolean;
  ColorVariants?: [
    {
      ColorName: string;
      ColorHex: string;
      FeaturedImageUrl: string;
      IsOwn: boolean;
    },
  ];
  shortDescription?: string;
  isCategoryVisible?: boolean;
  neuroMarketingMessage?: [
    {
      Id: string;
      RuleName: string;
      Message: string;
      Scope: number;
      Gender: string;
      Icon: string;
    },
  ];
  neuroMarketingSettings?: {
    EnableRotation?: boolean;
    RotationTimeSeconds?: number;
  };
  HasFreeShipping?: boolean;
  AvailableInProvidedStore?: boolean;
}

const ProductCard = ({
  className = '',
  name,
  url,
  imageUrl,
  imageAlt,
  imageAspectRatio,
  category,
  options,
  oldPrice,
  price,
  seName,
  isCategoryProduct,
  colorVariants,
  largeSize,
  ColorVariants,
  topRightIconClassName,
  topRightIcon,
  shortDescription,
  isCategoryVisible,
  neuroMarketingMessage,
  neuroMarketingSettings,
  HasFreeShipping,
  AvailableInProvidedStore = false,
}: Partial<IProduct> & IProductCard) => {
  const { isQuickViewEnabled, prepareQuickView, showQuickView } = useProductQuickView(seName);
  const [checked, setChecked] = useState('');

  const [selectedImage, setSelectedImage] = useState('');
  const [availableColor, setAvailableColor] = useState(ColorVariants);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (colorVariants) {
      availableColor?.map((color) => {
        if (color.IsOwn) {
          setSelectedImage(color.FeaturedImageUrl);
          setChecked(color.ColorHex);
        }
      });
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (colorVariants) {
      availableColor?.map((color) => {
        if (checked == color.ColorHex) {
          setSelectedImage(color.FeaturedImageUrl);
          setChecked(color.ColorHex);
        }
      });
    }
  }, [checked]);

  function getHeight() {
    if (typeof document !== 'undefined') {
      const el = document.getElementById(seName + 'product-card');
      const divHeight = Number(el?.scrollHeight || 1);
      return divHeight;
    } else {
      return 1;
    }
  }

  const maxHeightOfDescription = Math.ceil(3 * 12 * 1.25 * 1.4);

  // Generate unique identifier for this product card
  const productIdentifier = seName || name?.replaceAll(/\s+/g, '-').toLowerCase();

  return (
    <div
      onMouseEnter={prepareQuickView}
      className={`product-card flex h-full flex-col justify-between ${className}`}
      data-testid={`product-card-${productIdentifier}`}
    >
      <div
        className={`image-container relative flex w-full overflow-hidden ${
          imageAspectRatio ?? 'aspect-square'
        } ${isCategoryProduct ? 'bg-[#ECEFF1]' : ''}`}
        data-testid={`product-image-container-${productIdentifier}`}
      >
        <div className="group flex w-full">
          <CustomLink prefetch={false} href={url} className="absolute inset-0" data-testid={`product-image-link-${productIdentifier}`}>
            <Image
              src={selectedImage !== '' ? selectedImage : `${imageUrl ? imageUrl : Default_Image}`}
              alt={imageAlt || ''}
              fill
              className="transition-transform lg:group-hover:scale-125"
              placeholder="blur"
              blurDataURL={base64Image}
              unoptimized={true}
              data-testid={`product-image-${productIdentifier}`}
            />
          </CustomLink>
          {isQuickViewEnabled && <QuickViewButton onClick={showQuickView} dataTestId={`quick-view-button-${productIdentifier}`} />}
        </div>
        {topRightIcon && (
          <div
            className={`z-9 absolute flex h-8 w-8 items-center justify-center ${
              topRightIconClassName ? `${topRightIconClassName}` : 'right-2 top-2'
            }`}
            data-testid={`top-right-icon-${productIdentifier}`}
          >
            {topRightIcon}
          </div>
        )}
      </div>
      <CustomLink prefetch={false} href={url} className={`group block px-1.5 pt-4`} data-testid={`product-title-link-${productIdentifier}`}>
        {isCategoryVisible !== false && category && (
          <p
            className={`mb-1 text-gray-400 ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-base sm:text-lg'}`}
            data-testid={`product-category-${productIdentifier}`}
          >
            {category}
          </p>
        )}
        <p
          className={`line-clamp-3 whitespace-normal group-hover:underline ${
            largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-base sm:text-lg'
          } `}
          data-testid={`product-name-${productIdentifier}`}
        >
          {name}
        </p>
        {price >= 0 && (
          <>
            <div className="flex items-center space-x-3">
              <p className="mt-1 flex items-center space-x-4">
                {Number(oldPrice) > Number(price) && (
                  <span className={`text-gray-300 line-through ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-lg sm:text-lg'}`}>
                    <span className="currency">৳</span>
                    {numberFormat(oldPrice)}
                  </span>
                )}
                <span className={`font-bold ${largeSize ? 'text-xl md:text-2xl lg:text-3xl' : 'text-sm sm:text-lg'}`}>
                  <span className="currency">৳</span>
                  {numberFormat(price)}
                </span>
              </p>
            </div>
            <RotatingNeuroMessage messages={neuroMarketingMessage} settings={neuroMarketingSettings} loaded={loaded} largeSize={largeSize} />
          </>
        )}
      </CustomLink>
      {largeSize && shortDescription && (
        <div className="relative" data-testid={`product-description-container-${productIdentifier}`}>
          <div
            className="text-cutoff relative overflow-hidden px-1.5 text-xl opacity-50"
            id={seName + 'product-card'}
            dangerouslySetInnerHTML={{ __html: shortDescription }}
            data-testid={`product-description-${productIdentifier}`}
          ></div>

          {loaded && getHeight() > maxHeightOfDescription && (
            <CustomLink
              prefetch={false}
              href={url}
              className="absolute bottom-0 right-0 block w-fit bg-white pl-16 pr-8 pt-1 text-right leading-7 text-gray-500 underline"
              style={{ background: 'linear-gradient(90deg, rgb(255 255 255 / 40%) 0%, #fff 25%)' }}
              data-testid={`continue-reading-link-${productIdentifier}`}
            >
              Continue Reading
            </CustomLink>
          )}
        </div>
      )}
      <div className="flex items-center gap-x-4">
        {colorVariants && (
          <div className="flex flex-row gap-x-3 px-1.5" data-testid={`color-variants-container-${productIdentifier}`}>
            {availableColor?.map((color, index) => (
              <ProductColorVariants
                key={color.ColorHex + index}
                name={color.ColorName}
                value={color.ColorHex}
                className=""
                checked={checked}
                setChecked={setChecked}
                largeSize={largeSize}
              />
            ))}
          </div>
        )}
        {AvailableInProvidedStore && (
          <div className="flex items-center gap-x-2 text-lg">
            <IconExpress className="h-14 w-14" />
          </div>
        )}
        {HasFreeShipping && (
          <div className="mt-2 flex items-center">
            <IconFreeShipping className="w-5" />
            <p className="ml-2 text-lg font-medium italic">Free Shipping</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
